// Customization & Add-ons CTA block.
// Tells the user that Elevations / MEP / Structural drawings are NOT part of
// the Phase-1 Full Concept Layout Package, and gives them a one-tap channel
// to request a custom quote from Team Mera Naksha.
//
// The WhatsApp deep-link pre-fills the design name so support immediately
// has context. The phone number comes from the WA_PHONE env (mirrored on
// the frontend via /api/site/config) — we fall back to the founder's WhatsApp
// for now until the env value is wired through.
import React from "react";
import { Mail, Plus } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { t } from "@/i18n";

const FALLBACK_WA = "919717667115";          // Customer Support · Team Mera Naksha
const FALLBACK_EMAIL = "support@meranaksha.com";

const buildWaUrl = (phone, msg) =>
  `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;

export const AddonsSection = ({
  lang,
  designName,
  waPhone = FALLBACK_WA,
  email = FALLBACK_EMAIL,
  testId = "addons-section",
}) => {
  const subject = designName
    ? `Add-ons request: ${designName}`
    : "Add-ons request for a Mera Naksha Concept Plan";
  const wMsg = `Namaste Team Mera Naksha 🙏\n\nI'd like a custom quote for add-ons on top of my Concept Plan${
    designName ? ` (${designName})` : ""
  }:\n• 2D Elevations\n• MEP Drawings\n• Structural Drawings\n\nPlease share scope, timeline and pricing. Thank you!`;
  const eMsg = `mailto:${email}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(wMsg)}`;

  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-gradient-to-br from-gold/15 via-white to-white border-2 border-gold/35 p-5"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-gold text-navy grid place-items-center shrink-0">
          <Plus className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold-dark font-extrabold">
            {t(lang, "addons_title")}
          </div>
          <p className="mt-1.5 text-[13.5px] text-navy/90 leading-relaxed">
            {t(lang, "addons_message")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={buildWaUrl(waPhone, wMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-wa`}
              className="inline-flex items-center gap-2 h-10 px-3.5 rounded-xl bg-whatsapp text-white text-[13px] font-extrabold shadow-[0_6px_14px_rgba(37,211,102,0.35)] hover:bg-whatsapp-hover transition-colors"
            >
              <FaWhatsapp className="h-4 w-4" />
              {t(lang, "addons_cta_wa")}
            </a>
            <a
              href={eMsg}
              data-testid={`${testId}-email`}
              className="inline-flex items-center gap-2 h-10 px-3.5 rounded-xl bg-white text-navy text-[13px] font-extrabold border-2 border-navy/15 hover:bg-navy hover:text-gold hover:border-navy transition-colors"
            >
              <Mail className="h-4 w-4" />
              {t(lang, "addons_cta_email")}
            </a>
          </div>
          <p className="mt-3 text-[11px] italic text-muted-foreground leading-snug">
            {t(lang, "addons_disclaimer")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddonsSection;
